import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRightLeft, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const LIBELLES = { remplissage: 'Encuvage', soutirage: 'Soutirage', assemblage: 'Assemblage', traitement: 'Traitement', analyse: 'Analyse', mise_bouteille: 'Mise en bouteille' };

function FormAssemblage({ cuves, onFait }) {
  const [src, setSrc] = useState('');
  const [dst, setDst] = useState('');
  const [vol, setVol] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const pleines = cuves.filter((c) => c.volume_actuel_hl > 0);
  const valider = async () => {
    setEnvoi(true);
    try {
      const r = await api.post(`/cave/cuves/${dst}/operation`, { type_operation: 'assemblage', cuve_source_id: src, volume_hl: parseFloat(vol), observations: `Assemblage de ${vol} hl` });
      if (r.data.operation?.alerte) toast.warning(`Assemblage enregistré — attention : ${r.data.operation.alerte}`);
      else toast.success('Assemblage enregistré au registre de cave');
      setVol('');
      onFait();
    } catch (e) { toast.error(e.response?.data?.detail || "Assemblage impossible"); }
    setEnvoi(false);
  };
  return (
    <div className="flex flex-wrap items-end gap-2 p-3 bg-purple-50 rounded-lg" data-testid="form-assemblage">
      <ArrowRightLeft className="w-5 h-5 text-purple-600 mb-2" />
      <Select value={src} onValueChange={(v) => { setSrc(v); if (v === dst) setDst(''); }}>
        <SelectTrigger className="w-64 bg-white" data-testid="assemblage-source"><SelectValue placeholder="Cuve source…" /></SelectTrigger>
        <SelectContent>
          {pleines.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`assemblage-source-${c.id}`}>{c.nom} — {c.contenu} ({c.volume_actuel_hl} hl)</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={dst} onValueChange={setDst}>
        <SelectTrigger className="w-64 bg-white" data-testid="assemblage-destination"><SelectValue placeholder="Cuve destination…" /></SelectTrigger>
        <SelectContent>
          {cuves.filter((c) => c.id !== src).map((c) => <SelectItem key={c.id} value={c.id} data-testid={`assemblage-destination-${c.id}`}>{c.nom} — {c.volume_actuel_hl}/{c.capacite_hl} hl</SelectItem>)}
        </SelectContent>
      </Select>
      <Input type="number" placeholder="Volume hl" value={vol} onChange={(e) => setVol(e.target.value)} className="w-28" data-testid="assemblage-volume" />
      <Button onClick={valider} disabled={!src || !dst || !vol || envoi} data-testid="assemblage-valider">
        {envoi && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Assembler
      </Button>
    </div>
  );
}

function Recettes() {
  const [recettes, setRecettes] = useState([]);
  useEffect(() => { api.get('/cave/recettes').then((r) => setRecettes(r.data.recettes || [])).catch(() => {}); }, []);
  if (!recettes.length) return null;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="recettes-vinification">
      {recettes.map((r) => (
        <div key={r.id} className="border rounded-lg p-3 bg-white text-sm">
          <p className="font-semibold">{r.nom}</p>
          <p className="text-xs text-gray-500">{r.cepages} · SO2 total cible {r.so2_total_cible_mg_l} mg/l</p>
          <ol className="list-decimal ml-5 mt-2 space-y-0.5">{r.etapes.map((e) => <li key={e}>{e}</li>)}</ol>
          {r.conseil_ia && <p className="mt-2 text-purple-800 bg-purple-50 rounded p-2 text-xs">Conseil de l'œnologue IA : {r.conseil_ia}</p>}
        </div>
      ))}
    </div>
  );
}

export function RegistreCave({ cuves, onChange }) {
  const { activeSociete } = useWorkspace();
  const [ops, setOps] = useState([]);
  const charger = useCallback(async () => {
    const r = await api.get('/cave/registre');
    setOps(r.data.registre || []);
  }, []);
  useEffect(() => { charger().catch(() => toast.error('Registre indisponible')); }, [charger, activeSociete?.id]);
  const nomCuve = (id) => cuves.find((c) => c.id === id)?.nom || '';
  return (
    <div className="p-4 space-y-4" data-testid="registre-cave">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Registre de cave</h2>
        <span className="text-sm text-gray-500" data-testid="registre-total">{ops.length} opérations</span>
      </div>
      <Recettes key={activeSociete?.id} />
      <FormAssemblage cuves={cuves} onFait={() => { charger(); onChange(); }} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-gray-500 border-b">
            <th className="py-2 pr-3">Date</th><th className="pr-3">Opération</th><th className="pr-3">Cuve(s)</th><th className="pr-3">Volume</th><th>Observations</th>
          </tr></thead>
          <tbody>
            {ops.map((o) => (
              <tr key={o.id} className="border-b last:border-0" data-testid={`registre-ligne-${o.id}`}>
                <td className="py-2 pr-3 whitespace-nowrap">{new Date(o.date_operation).toLocaleDateString('fr-FR')}</td>
                <td className="pr-3"><span className={`px-2 py-0.5 rounded text-xs ${o.type_operation === 'assemblage' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100'}`}>{LIBELLES[o.type_operation] || o.type_operation}</span></td>
                <td className="pr-3">{o.type_operation === 'assemblage' ? `${o.cuve_source_nom || nomCuve(o.cuve_source_id)} → ${o.cuve_destination_nom || nomCuve(o.cuve_id)}` : nomCuve(o.cuve_id)}</td>
                <td className="pr-3">{o.volume_hl ? `${o.volume_hl} hl` : o.dose ? `${o.dose} g/hl` : '—'}</td>
                <td className="text-gray-600">{o.observations}{o.alerte && <span className="block text-amber-700 text-xs" data-testid={`registre-alerte-${o.id}`}>⚠ {o.alerte}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
